import { AlertCircle, Check, Loader2, Plus, Sparkles, Square, X } from "lucide-react"
import { type ReactNode, type Ref, useEffect, useImperativeHandle, useRef, useState } from "react"
import { cancelGenerationJob } from "@/components/jobs/cancel-generation-job"
import { JobElapsedTimer, formatElapsed, jobKindLabel, statusDetailLabel } from "@/components/jobs/job-elapsed-timer"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import type { GenerationPriceView } from "@/hooks/use-generation-price"
import { DEFAULT_CONFIRM_DELAY_SECONDS, useConfirmDelayAction } from "@/hooks/use-confirm-delay-action"
import { useProgressClock } from "@/hooks/use-progress-clock"
import { useTranslation } from "@/hooks/use-translation"
import { estimatedJobProgress, isEstimatedGenerationPhase } from "@/lib/job-estimated-progress"
import { confirm } from "@/lib/confirm"
import { cn } from "@/lib/utils"
import { useWorkspaceJobsStore } from "@/stores/workspace-jobs-store"
import type { GenerationJobRow } from "@/types/workspace"

/** Бегущий процесс без GenerationJob (регистрация ассета и т.п.): заголовок, подэтап, таймер. */
export type GenerationBarStatus = {
  title?: string | null
  statusDetail?: string | null
  startedAt?: string | null
}

export type GenerationBarHandle = {
  request: () => void
  /** Без отсчёта — для горячих клавиш (⌘↵ на холсте). */
  fireNow: () => void
}

/** Сколько держим «Готово» после того, как задача ушла из активных. */
const DONE_FLASH_MS = 3000

/** Тихий параметр генерации: модель, формат, длительность. Кнопкой — если открывает выбор. */
export const GENERATION_PARAM_CLASS =
  "inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-xs whitespace-nowrap text-ui-text-secondary"

export function GenerationParam({ children, title }: { children: ReactNode; title?: string }) {
  return (
    <span className={GENERATION_PARAM_CLASS} title={title}>
      {children}
    </span>
  )
}

type GenerationBarProps = {
  /**
   * stack — док под промптом: параметры строкой сверху, кнопка во всю ширину снизу.
   * row — пульт под ассетом: параметры слева, кнопка справа в одной строке.
   */
  layout?: "stack" | "row"
  /** Параметры генерации (модель, формат, длительность…). Пока идёт генерация — приглушены. */
  params?: ReactNode
  label: string
  cost?: GenerationPriceView | null
  onClick: () => void | boolean | Promise<void | boolean>
  confirmDelayKey: string
  confirmDelaySeconds?: number
  onBeforeArm?: () => boolean | Promise<boolean>
  /** Запрос на запуск ещё в пути (до появления задачи). */
  busy?: boolean
  disabled?: boolean
  /** Почему нельзя запустить — пишется в кнопке вместо цены. */
  disabledReason?: string | null
  activeJob?: GenerationJobRow | null
  status?: GenerationBarStatus | null
  /** Заголовок бегущей задачи, когда это не обычная генерация (например, «Корректировка эскиза»). */
  runningTitle?: string | null
  /** Для «Остановить»: без проекта кнопки стопа нет. */
  projectId?: number | null
  /** «+» в бегущей полосе: запустить ещё одну такую же генерацию (после подтверждения в модалке). */
  allowParallel?: boolean
  /** Сколько генераций этого ассета идёт сейчас; больше одной — счётчик на полосе. */
  runningCount?: number
  /** Ошибка последней генерации; показывается, пока не запущена новая или не закрыта. */
  error?: string | null
  /** Параметры переносятся на вторую строку вместо обрезки (у холста их много). */
  paramsWrap?: boolean
  /** Сегменты справа от кнопки в покое (например, «Финал» черновика). */
  trailing?: ReactNode
  actionRef?: Ref<GenerationBarHandle | null>
  className?: string
}

export function GenerationBar({
  layout = "stack",
  params,
  label,
  cost = null,
  onClick,
  confirmDelayKey,
  confirmDelaySeconds = DEFAULT_CONFIRM_DELAY_SECONDS,
  onBeforeArm,
  busy = false,
  disabled = false,
  disabledReason,
  activeJob = null,
  status = null,
  runningTitle = null,
  projectId = null,
  allowParallel = true,
  runningCount = 1,
  error = null,
  trailing,
  actionRef,
  className,
  paramsWrap = false,
}: GenerationBarProps) {
  const { t } = useTranslation(["common", "jobs"])
  const running = Boolean(activeJob || status)
  const { countdownLeft, isArmed, isPending, request, fireNow } = useConfirmDelayAction({
    persistKey: confirmDelayKey,
    seconds: confirmDelaySeconds,
    disabled: busy || (disabled && !running),
    onFire: onClick,
    onBeforeArm,
  })
  useImperativeHandle(
    actionRef,
    () => ({
      request,
      fireNow: () => {
        if (busy || disabled || isPending) return
        fireNow()
      },
    }),
    [request, fireNow, busy, disabled, isPending],
  )

  // Ещё одна генерация поверх идущей — осознанное действие с ценой: подтверждение в модалке,
  // а не отсчёт на маленькой кнопке.
  const confirmParallel = async () => {
    const count = Math.max(1, runningCount)
    const price = cost?.status === "ready" && cost.label ? cost.label : null
    const ok = await confirm({
      title: t("generationBar.parallelTitle"),
      message: [
        t("generationBar.parallelRunning", { count }),
        price ? t("generationBar.parallelCost", { price }) : t("generationBar.parallelNoCost"),
      ].join("\n"),
      confirmLabel: t("generationBar.parallelConfirm"),
    })
    if (ok && !busy && !isPending) fireNow()
  }

  const done = useDoneFlash(activeJob)
  const [dismissedError, setDismissedError] = useState<string | null>(null)
  useEffect(() => {
    if (running) setDismissedError(null)
  }, [running])
  const visibleError = !running && error?.trim() && error !== dismissedError ? error.trim() : null

  const stack = layout === "stack"
  const barHeight = stack ? "h-10" : "h-12"
  const paramsRow = params ? (
    <div
      className={cn(
        "flex min-w-0 items-center gap-0.5",
        paramsWrap ? "flex-wrap gap-y-1" : "overflow-hidden",
        running && "pointer-events-none opacity-45",
        !stack && "flex-1",
      )}
    >
      {params}
    </div>
  ) : null

  const segmentClass =
    "relative flex shrink-0 items-center justify-center gap-1.5 border-l border-white/[0.08] px-3 text-xs text-ui-text-secondary transition-colors hover:bg-ui-surface-hover hover:text-ui-text-primary light:border-black/[0.08]"

  let body: ReactNode
  if (running) {
    body = (
      <RunningTrack
        job={activeJob}
        status={runningTitle ? { ...status, title: runningTitle } : status}
        heightClass={barHeight}
        segmentClass={segmentClass}
        stopLabel={t("jobs:stopGeneration")}
        onStop={
          activeJob && projectId != null
            ? () => void cancelGenerationJob(projectId, activeJob.id, t("jobs:stopGenerationConfirm"))
            : null
        }
        runningCount={runningCount}
        parallel={
          allowParallel && !disabled
            ? {
                label: t("jobs:startParallelGeneration"),
                pending: busy || isPending,
                onClick: () => void confirmParallel(),
              }
            : null
        }
      />
    )
  } else if (done && !error?.trim()) {
    body = (
      <div className={cn("relative flex items-stretch overflow-hidden rounded-lg bg-ui-accent/15", barHeight)}>
        <div className="flex min-w-0 flex-1 items-center gap-2 px-3 text-[13px] text-ui-text-primary">
          <Check className="h-3.5 w-3.5 shrink-0 text-ui-status-approved" strokeWidth={2.5} />
          <span className="truncate">{t("jobs:status.completed")}</span>
          {done.elapsedMs != null ? (
            <span className="font-mono text-[11px] text-ui-text-secondary tabular-nums">
              {formatElapsed(done.elapsedMs)}
            </span>
          ) : null}
        </div>
      </div>
    )
  } else if (visibleError) {
    body = (
      <div className={cn("relative flex items-stretch overflow-hidden rounded-lg bg-ui-surface-raised", barHeight)}>
        <div className="flex min-w-0 flex-1 items-center gap-2 px-3 text-xs text-ui-text-primary" title={visibleError}>
          <AlertCircle className="h-3.5 w-3.5 shrink-0 text-ui-status-failed" />
          <span className="truncate">{visibleError}</span>
        </div>
        <button
          type="button"
          className={segmentClass}
          aria-label={t("generationBar.dismissError")}
          onClick={() => setDismissedError(error ?? null)}
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          className={cn(segmentClass, "text-ui-text-primary")}
          disabled={busy || isPending || disabled}
          onClick={request}
        >
          {busy || isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          {isArmed && countdownLeft != null
            ? t("cancelCountdown", { seconds: countdownLeft })
            : t("generationBar.retry")}
        </button>
      </div>
    )
  } else {
    body = (
      <GenerateButton
        label={label}
        cost={cost}
        heightClass={stack ? "h-10 w-full" : "h-9"}
        disabled={disabled}
        disabledReason={disabledReason}
        pending={busy || isPending}
        armedLabel={isArmed && countdownLeft != null ? t("cancelCountdown", { seconds: countdownLeft }) : null}
        onClick={request}
        trailing={trailing}
      />
    )
  }

  if (stack) {
    return (
      <div className={cn("flex w-full min-w-0 flex-col gap-2", className)}>
        {paramsRow}
        {body}
      </div>
    )
  }

  if (running || (done && !error?.trim()) || visibleError) {
    return <div className={cn("w-full min-w-0", className)}>{body}</div>
  }

  return (
    <div
      className={cn(
        "flex h-12 w-full min-w-0 items-center gap-1 rounded-lg bg-ui-surface-raised px-1.5 light:bg-ui-surface-sunken",
        className,
      )}
    >
      {paramsRow ?? <div className="flex-1" />}
      <div className="ml-auto flex shrink-0 items-center">{body}</div>
    </div>
  )
}

function GenerateButton({
  label,
  cost,
  heightClass,
  disabled,
  disabledReason,
  pending,
  armedLabel,
  onClick,
  trailing,
}: {
  label: string
  cost: GenerationPriceView | null
  heightClass: string
  disabled: boolean
  disabledReason?: string | null
  pending: boolean
  armedLabel: string | null
  onClick: () => void
  trailing?: ReactNode
}) {
  const { t } = useTranslation("common")
  const armed = armedLabel != null
  const off = disabled && !armed
  const visibleCost = cost != null && cost.status !== "hidden" ? cost : null
  const meta = off ? (
    disabledReason ? (
      <span className="truncate">{disabledReason}</span>
    ) : null
  ) : visibleCost?.status === "loading" ? (
    <Loader2 className="h-3 w-3 animate-spin" aria-label={t("generationPriceHint")} />
  ) : visibleCost?.status === "ready" && visibleCost.label ? (
    <span className="tabular-nums">{visibleCost.label}</span>
  ) : null

  return (
    <div className={cn("flex min-w-0 items-stretch", heightClass.includes("w-full") && "w-full")}>
      <button
        type="button"
        disabled={off || pending}
        onClick={onClick}
        title={off && disabledReason ? disabledReason : undefined}
        className={cn(
          "flex min-w-0 flex-1 items-center gap-2 overflow-hidden rounded-lg pl-3 text-[13px] font-medium whitespace-nowrap transition-colors",
          heightClass,
          trailing && "rounded-r-none",
          armed
            ? "border border-ui-accent bg-transparent pr-3 text-ui-text-primary"
            : off
              ? "bg-ui-surface-raised text-ui-text-muted"
              : "active:bg-ui-accent-press bg-ui-accent text-ui-on-accent hover:bg-ui-accent-hover",
          !meta && !armed && "pr-3",
        )}
      >
        {pending ? (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
        ) : armed ? (
          <X className="h-3.5 w-3.5 shrink-0" />
        ) : (
          <Sparkles className="h-3.5 w-3.5 shrink-0" />
        )}
        <span className="truncate">{armedLabel ?? label}</span>
        {meta && !armed ? (
          <span
            className={cn(
              "ml-auto flex h-full min-w-0 shrink items-center border-l px-3 text-xs font-normal",
              off
                ? "border-white/[0.06] text-ui-text-muted light:border-black/[0.06]"
                : "border-black/20 text-ui-accent-text",
            )}
          >
            {meta}
          </span>
        ) : null}
      </button>
      {trailing}
    </div>
  )
}

function RunningTrack({
  job,
  status,
  heightClass,
  segmentClass,
  stopLabel,
  onStop,
  runningCount,
  parallel,
}: {
  job: GenerationJobRow | null
  status: GenerationBarStatus | null
  heightClass: string
  segmentClass: string
  stopLabel: string
  onStop: (() => void) | null
  runningCount: number
  parallel: { label: string; pending: boolean; onClick: () => void } | null
}) {
  const { t } = useTranslation("common")
  const now = useProgressClock(job ? isEstimatedGenerationPhase(job) : false)
  const progress = job ? estimatedJobProgress(job, now) : 0
  const detailKey = status?.statusDetail ?? job?.status_detail ?? null
  const detail = detailKey ? statusDetailLabel(detailKey) : null
  const queued = job?.status === "queued"
  const phase = detail ?? (queued ? statusDetailLabel("provider_queued") : null)
  const heading =
    status?.title && phase
      ? `${status.title} · ${phase}`
      : (status?.title ?? phase ?? (job ? jobKindLabel(job.job_kind) : ""))
  const timerStart = status?.startedAt ?? job?.started_at ?? job?.created_at ?? null

  return (
    <div
      className={cn("relative flex w-full items-stretch overflow-hidden rounded-lg bg-ui-surface-raised", heightClass)}
    >
      {progress > 0 ? (
        <div
          className="absolute inset-y-0 left-0 bg-ui-accent/35 transition-[width] duration-700 ease-out"
          style={{ width: `${Math.min(100, progress)}%` }}
          aria-hidden
        />
      ) : null}
      <div
        className="relative flex min-w-0 flex-1 items-center gap-2 px-3 text-[13px] text-ui-text-primary"
        role="status"
        aria-live="polite"
      >
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-ui-accent-text" />
        <span className="truncate">{heading}</span>
        {timerStart ? (
          <JobElapsedTimer
            startedAt={timerStart}
            active
            className="shrink-0 font-mono text-[11px] text-ui-text-secondary [&_svg]:hidden"
          />
        ) : null}
        {runningCount > 1 ? (
          <span
            className="shrink-0 rounded-sm bg-ui-accent/30 px-1.5 py-0.5 text-[11px] leading-none text-ui-text-primary tabular-nums"
            title={t("generationBar.runningCountHint", { count: runningCount })}
          >
            {t("generationBar.runningCount", { count: runningCount })}
          </span>
        ) : null}
        {progress > 0 ? (
          <span className="ml-auto shrink-0 font-mono text-[11px] text-ui-text-secondary tabular-nums">
            {progress}%
          </span>
        ) : null}
      </div>
      {onStop ? (
        <ActionHintTooltip label={stopLabel} className="items-stretch">
          <button type="button" className={cn(segmentClass, "w-10 px-0")} aria-label={stopLabel} onClick={onStop}>
            <Square className="h-3 w-3" />
          </button>
        </ActionHintTooltip>
      ) : null}
      {parallel ? (
        <ActionHintTooltip label={parallel.label} className="items-stretch">
          <button
            type="button"
            className={cn(segmentClass, "w-10 px-0")}
            aria-label={parallel.label}
            disabled={parallel.pending}
            onClick={parallel.onClick}
          >
            {parallel.pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          </button>
        </ActionHintTooltip>
      ) : null}
    </div>
  )
}

/** «Готово» на пару секунд после того, как активная задача ушла (и не упала). */
function useDoneFlash(activeJob: GenerationJobRow | null): { elapsedMs: number | null } | null {
  const lastRef = useRef<GenerationJobRow | null>(null)
  const [done, setDone] = useState<{ elapsedMs: number | null } | null>(null)

  useEffect(() => {
    if (activeJob) {
      lastRef.current = activeJob
      setDone(null)
      return
    }
    const last = lastRef.current
    lastRef.current = null
    if (!last) return
    // Задача ушла из активных — это ещё не успех: она могла упасть, быть остановлена,
    // или пульт просто переключили на другой шот. «Готово» — только по итоговому статусу в store.
    const final = useWorkspaceJobsStore.getState().jobs.find((job) => job.id === last.id)
    if (final?.status !== "completed") return
    const start = final.started_at ?? final.created_at
    const end = final.updated_at ? new Date(final.updated_at).getTime() : Date.now()
    setDone({ elapsedMs: start ? end - new Date(start).getTime() : null })
    const id = window.setTimeout(() => setDone(null), DONE_FLASH_MS)
    return () => window.clearTimeout(id)
  }, [activeJob])

  return done
}
